import React, { useCallback, useMemo, useRef } from "react";
import { useCategoryContext } from "../Context";
import { HorizontalTwoLineLabelKpiProviders } from "../categoryChartQueryFns";
import { Paragraph } from "../Paragraph";
import { CategoryTheme, PROVIDER_TITLE_CLASS } from "../misc";
import { Figure } from "../Figure";
import { getHighlightColors, getTraceColors } from "../../themes";
import { CategoryChart, prefixValue, suffixValue } from "../processors";
import _ from "lodash";
import {
    UPDATE_CLASSNAME,
    WATCH_CLASSNAME,
    HorizontalTwoLineLabelWithData,
    HorizontalTwoLineLabelWithDataProps
} from "../HorizontalTwoLineLabel";
import styles from "./HorizontalTwoLineLabelProviders.module.scss";

interface HorizontalTwoLineLabelProvidersProps extends HorizontalTwoLineLabelKpiProviders {
    categoryChart: CategoryChart;
}
export const HorizontalTwoLineLabelProviders: React.FC<HorizontalTwoLineLabelProvidersProps> = ({
    title,
    footer,
    unit,
    elements,
    waterProviders,
    categoryChart
}) => {
    const { category } = useCategoryContext();
    const ref = useRef<HTMLDivElement>();

    // sort data, each one if it doesn't exist from the other, add 0
    const tgraphs: HorizontalTwoLineLabelWithDataProps[] = useMemo(() => {
        // const labels = _.uniq(
        //     _.flattenDeep(
        //         elements?.map(element =>
        //             element?.graph?.data
        //                 ?.filter(d => d.value != null && d.value !== 0 && typeof d.value !== "string")
        //                 ?.map(d => d.label)
        //         )
        //     )
        // ).filter(d => d != null);

        const maxValue = _.max(
            elements?.map(element =>
                _.max(
                    _.values(_.groupBy(element?.graph?.data, d => d.label)).map(group => {
                        return _.sum(group.map(piece => (typeof piece.value === "string" ? 0 : piece.value ?? 0)));
                    })
                )
            )
        );

        const series = _.uniq(
            _.flattenDeep(
                elements?.map(element =>
                    element?.graph?.data
                        ?.filter(d => d.value != null && d.value !== 0 && typeof d.value !== "string")
                        ?.map(d => d.seriesName)
                )
            )
        ).filter(d => d != null);

        const newElements = elements?.map(element => {
            const data = element?.graph?.data?.filter(d => d != null && typeof d !== "string");
            if (!data?.length) return null;

            const descSumData = _.orderBy(
                _.entries(_.groupBy(data, d => d.label)).map(values => {
                    const label = values[0];
                    const group = values[1];
                    return {
                        label,
                        value: _.sum(group.map(piece => (typeof piece.value === "string" ? 0 : piece.value ?? 0)))
                    };
                }),

                // data?.filter(d => d?.value != null && d?.value !== 0 && typeof d.value !== "string"),
                d => d.value,
                "desc"
            );
            // const descLabels = descSumData
            //     ?.map(d => d.label)
            //     ?.reduce((prev, curr) => {
            //         prev[curr] = 1;
            //         return prev;
            //     }, {});

            // const zeroData = labels
            //     ?.filter(label => descLabels[label] !== 1)
            //     ?.map(label => {
            //         return {
            //             label,
            //             value: 0,
            //             seriesName: series?.[0]
            //         };
            //     });

            const newData = [
                ...data
                // ...zeroData
            ];

            const orderedLabels = _.uniq([
                ...descSumData?.filter(d => d.value != null && d.value !== 0)?.map(d => d.label)
                // ...zeroData.map(d => d.label)
            ]);

            return {
                dataPoints: newData
                    .map(d => {
                        return {
                            label: d.label,
                            value: d.value,
                            series: d.seriesName
                        };
                    })
                    .filter(d => d.value != null && typeof d.value !== "string"),
                labels:
                    // one provider, then abc ordering, otherwise order by values
                    elements?.length === 1 ? _.orderBy(orderedLabels, d => d, "asc") : orderedLabels,
                series,
                maxValue
            } as HorizontalTwoLineLabelWithDataProps;
        });
        // .filter(d => d != null);

        return newElements;
    }, [elements]);

    const queryHeightWatchingElements: () => {
        watching: HTMLElement[];
        update: HTMLElement[];
    } = useCallback(() => {
        // console.log(WATCH_CLASSNAME);
        const watching = Array.from(ref?.current?.querySelectorAll(`.${WATCH_CLASSNAME}`) as NodeListOf<HTMLElement>);
        const update = Array.from(ref?.current?.querySelectorAll(`.${UPDATE_CLASSNAME}`) as NodeListOf<HTMLElement>);
        return {
            watching,
            update
        };
    }, []);

    const series = useMemo(() => {
        return _.orderBy(
            _.uniq(_.flattenDeep(tgraphs?.map(d => d?.series))?.filter(d => d != null && d.trim() !== "")),
            d => d,
            "asc"
        );
    }, [tgraphs]);

    if (!category) return null;

    const theme = category.theme as CategoryTheme;
    const seriesBackground = getTraceColors(theme, series?.length ?? 0, series ?? []);

    return (
        <div className="row" ref={ref}>
            {title != null && <Paragraph title={""} status={true} nid={0} text={title} className="col-12" />}
            {series?.length >= 1 && (
                <div className={`${styles.legend} col-12`}>
                    {series?.map((seri, index) => {
                        return (
                            <div key={index} className={styles.legendItem}>
                                <div
                                    style={{
                                        background: seriesBackground[index]
                                    }}
                                    className={styles.legendColorBox}
                                ></div>
                                {seri}
                            </div>
                        );
                    })}
                </div>
            )}
            {elements?.map((element, index) => {
                const figure = element?.figure;
                const graph = element?.graph;

                return (
                    element != null &&
                    waterProviders?.[index] != null && (
                        <div className={`col-12 col-lg-${12 / waterProviders?.length}`} key={index}>
                            {waterProviders?.length > 1 && (
                                <div className="headline">
                                    <div className={`${PROVIDER_TITLE_CLASS}`}>{waterProviders[index].name}</div>
                                </div>
                            )}
                            {/* ${
                                // in comparison, centralise the chart headline
                                _elements?.length > 1 ? styles.center : ""
                            } */}
                            {element?.graph?.header && (
                                <Paragraph
                                    nid={0}
                                    status={true}
                                    title={""}
                                    text={element?.graph?.header}
                                    className={`
                                        marginBottom 
                                        headline 
                                        chart-headline
                                        ${waterProviders?.length > 1 ? "text-center" : ""}
                                        `}
                                />
                            )}

                            {figure != null && (
                                <Figure
                                    unit={unit}
                                    categoryChart={categoryChart}
                                    figureStyle={{
                                        // getting colors for water provider
                                        color: getHighlightColors(theme, index + 1)?.[index]
                                    }}
                                    className="w-100"
                                    decimals={figure?.decimals}
                                    header={
                                        !figure?.header ? null : (
                                            <Paragraph title={""} status={true} nid={0} text={figure?.header} />
                                        )
                                    }
                                    footer={
                                        !figure?.footer ? null : (
                                            <Paragraph title={""} status={true} nid={0} text={figure?.footer} />
                                        )
                                    }
                                    value={figure?.value}
                                    prefix={prefixValue(figure.value, figure.unit)}
                                    suffix={suffixValue(figure.value, figure.unit)}
                                    unitInSuffix={figure?.unit}
                                />
                            )}

                            {figure == null && graph.data !== null && tgraphs[index] != null && (
                                <HorizontalTwoLineLabelWithData
                                    {...tgraphs[index]}
                                    unit={unit}
                                    queryHeightWatchingLabels={queryHeightWatchingElements}
                                    queryWidthWatchingLabels={queryHeightWatchingElements}
                                    theme={theme}
                                    onHover={undefined}
                                />
                            )}
                        </div>
                    )
                );
            })}

            {tgraphs?.find(d => d != null) != null &&
                elements?.find(element => element != null)?.graph?.footer != null &&
                elements?.find(element => element != null)?.graph?.footer != null && (
                    <Paragraph
                        nid={0}
                        status={true}
                        title={""}
                        text={elements?.find(element => element != null)?.graph?.footer}
                        className={`col-12 ${styles.chartFooter}`}
                    />
                )}

            {footer != null && <Paragraph title={""} status={true} nid={0} text={footer} className="col-12" />}
        </div>
    );
};
